import { useEffect, useRef } from "react";
import { subscribeLive } from "./liveSocket";

/**
 * For detail pages: did this live update touch record `id` of `topic`?
 * True after a reconnect (`changes` null) or a whole-topic change too.
 */
export const touches = (changes, topic, id) => {
  if (!changes) return true;
  const change = changes[topic];
  if (!change) return false;
  return change.all || change.ids.includes(id) || change.deleted.includes(id);
};

/**
 * Re-read a screen's data when another till / tab / user changes it.
 *
 *   useLiveData(["invoices", "clients"], () => loadInvoices({ silent: true }));
 *
 * `reload(changes)` gets `{ [topic]: { ids, deleted, all } }` for the topics
 * that changed, or `null` after a reconnect ("re-read everything"). Most
 * screens just re-run their normal load and ignore the argument.
 *
 * Topics: items, categories, units, mappings, stock (ids = ITEM ids),
 * invoices, purchases, stockAdjustments, returns, clients, suppliers, staff,
 * staffCommissions, employees, shifts, orderSessions, cashTransactions,
 * futurePrices, meetings, programLogs, company, globalSettings,
 * subscriptions, profiles, users, userTypes.
 *
 * Calls are coalesced: a burst (busy till) triggers at most one reload per
 * `minIntervalMs`, so a heavy screen isn't hammered. Pass `enabled: false`
 * to pause (e.g. while a form on the screen has unsaved edits).
 */
export default function useLiveData(topics, reload, { minIntervalMs = 1500, enabled = true } = {}) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const topicKey = [...topics].sort().join(",");

  useEffect(() => {
    if (!enabled) return undefined;
    const wanted = new Set(topicKey.split(",").filter(Boolean));
    let timer = null;
    let lastRun = 0;
    let pending = {};
    let resync = false;

    const run = () => {
      timer = null;
      lastRun = Date.now();
      const changes = resync ? null : pending;
      pending = {};
      resync = false;
      try {
        const result = reloadRef.current?.(changes);
        if (result && typeof result.catch === "function") {
          result.catch((error) => console.warn("Live reload failed:", error?.message || error));
        }
      } catch (error) {
        console.warn("Live reload failed:", error?.message || error);
      }
    };

    const unsubscribe = subscribeLive((event, payload) => {
      if (event === "resync") {
        resync = true;
      } else if (event === "changed") {
        let relevant = false;
        Object.entries(payload || {}).forEach(([topic, change]) => {
          if (!wanted.has(topic)) return;
          relevant = true;
          const merged = pending[topic] || { ids: [], deleted: [], all: false };
          pending[topic] = {
            ids: [...new Set([...merged.ids, ...(change.ids || [])])],
            deleted: [...new Set([...merged.deleted, ...(change.deleted || [])])],
            all: merged.all || !!change.all,
          };
        });
        if (!relevant) return;
      } else {
        return;
      }
      if (timer) return;
      const wait = Math.max(0, minIntervalMs - (Date.now() - lastRun));
      timer = setTimeout(run, wait);
    });

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [topicKey, minIntervalMs, enabled]);
}
